/**
 * The user's side of a support capture: one line per route change and per click on something
 * clickable, so the support portal can tell what the user did rather than infer it from requests.
 *
 * No values: a route keeps only its path (as clientNetworkCapture describes our own paths), with
 * codes, tokens and emails in it replaced. A click names the element by tag, role, data-testid,
 * link path and the region it sits in (sidebar, header, dialog, menu, page), never by its text,
 * label or input value, since those hold client names. A button with no data-testid is named by
 * the MUI icon inside it (MUI tags each icon, e.g. EditIcon), which is fixed per button.
 */

import { describeRequestUrl, type NetworkLogger } from './clientNetworkCapture';

type HistoryMethod = History['pushState'];

interface InteractionCaptureState {
  log: NetworkLogger;
  pushState: HistoryMethod;
  replaceState: HistoryMethod;
  onPopState: () => void;
  onClick: (event: MouseEvent) => void;
  lastPath: string;
}

let state: InteractionCaptureState | null = null;

// UI routes whose last segments are a credential or an email rather than an id.
const SECRET_IN_ROUTE =
  /(^|\/)(forgotPassword|signup|adminSignup|accept-invitation|reject-invitation|reject|advisor\/invitation)\/[^/]{1,512}/g;

const INTERACTIVE =
  'a[href], button, summary, select, label, input[type="checkbox"], input[type="radio"], input[type="submit"], input[type="button"], [role="button"], [role="link"], [role="menuitem"], [role="tab"], [role="option"], [role="checkbox"], [role="switch"]';

export function describeRoute(url: string, origin: string = window.location.origin): string {
  return describeRequestUrl(url, origin).replace(
    SECRET_IN_ROUTE,
    (_m, lead: string, route: string) => `${lead}${route}/<value>`,
  );
}

// A data-testid that looks like free text rather than a fixed id is left out entirely.
export function describeTestId(testId: string): string | null {
  return /[\s@]/.test(testId) || testId.length > 80 ? null : testId;
}

function regionOf(element: Element): string {
  const marked = element.closest('[data-capture-region]');
  if (marked) return marked.getAttribute('data-capture-region') ?? 'page';
  if (element.closest('[role="dialog"]')) return 'dialog';
  if (element.closest('[role="menu"], [role="listbox"]')) return 'menu';
  if (element.closest('header')) return 'header';
  return 'page';
}

export function describeClickTarget(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null;
  const element = target.closest(INTERACTIVE) ?? target.closest('[data-testid]');
  if (!element) return null;
  const parts = [element.tagName.toLowerCase()];
  const role = element.getAttribute('role');
  if (role) parts.push(`role=${role}`);
  const testId = element.getAttribute('data-testid');
  const safeTestId = testId ? describeTestId(testId) : null;
  if (safeTestId) parts.push(`testid=${safeTestId}`);
  const icon = element.querySelector('svg[data-testid$="Icon"]')?.getAttribute('data-testid');
  if (icon && icon !== testId) parts.push(`icon=${icon}`);
  const href = element.getAttribute('href');
  if (href && !href.startsWith('#')) parts.push(`href=${describeRoute(href)}`);
  return `${parts.join(' ')} in ${regionOf(element)}`;
}

function logRoute(how: string) {
  if (!state) return;
  const path = describeRoute(window.location.href);
  if (path === state.lastPath) return;
  state.lastPath = path;
  state.log('info', `[nav] ${how} ${path}`);
}

export function isInteractionCaptureActive(): boolean {
  return state !== null;
}

export function startInteractionCapture(log: NetworkLogger): void {
  if (state) return;
  const current: InteractionCaptureState = {
    log,
    pushState: window.history.pushState,
    replaceState: window.history.replaceState,
    onPopState: () => logRoute('back/forward'),
    onClick: (event: MouseEvent) => {
      const description = describeClickTarget(event.target);
      if (description) current.log('info', `[click] ${description}`);
    },
    lastPath: '',
  };
  state = current;

  window.history.pushState = function pushState(this: History, ...args) {
    current.pushState.apply(this, args);
    logRoute('push');
  };
  window.history.replaceState = function replaceState(this: History, ...args) {
    current.replaceState.apply(this, args);
    logRoute('replace');
  };
  window.addEventListener('popstate', current.onPopState);
  // Capture phase, so a handler that stops propagation still leaves its click in the log.
  document.addEventListener('click', current.onClick, true);
  logRoute('at');
}

export function stopInteractionCapture(): void {
  if (!state) return;
  window.history.pushState = state.pushState;
  window.history.replaceState = state.replaceState;
  window.removeEventListener('popstate', state.onPopState);
  document.removeEventListener('click', state.onClick, true);
  state = null;
}
